import { FormattedNumberInputField, FormGroupHeader } from "@/components/shared";
import { OrganizationFragmentDoc } from "@/graphql/__generated__/graphql";
import { useUpdateAdvancedCostParametersMutationHook } from "@/hooks/mutations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { isManager } from "@/utils/authentication";
import useUserStore from "@/store/shield";
import { ADVANCED_COST_PARAMETERS_CONFIG } from "@/constants/advancedCostParameters";
import { useFragment } from "@apollo/client/react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { InfoCircledIcon } from "@radix-ui/react-icons";

const createFormSchema = (t: TFunction) => {
  // Base number schema with common validation messages
  const baseNumber = z.number({
    error: (issue) => issue.input === undefined ?
      t("schemas:form.required")
      :
      t("schemas:form.invalid"),
  });

  // Integer schema that must be non-negative
  const nonNegativeInt = baseNumber.int().nonnegative();

  // Number schema that must be non-negative
  const nonNegativeNum = baseNumber.nonnegative();

  return z.object({
    averageHoursWorkedPerMonth: nonNegativeInt,
    trainingDays: nonNegativeInt,
    yearlyTrainingCosts: nonNegativeNum,
    sickDays: nonNegativeInt,
    employeeFluctuation: baseNumber.min(0).max(100, { message: t("schemas:form.atmax100") }),
    yearlyUniformCosts: nonNegativeNum,
    otherMaterialCosts: nonNegativeNum,
    averageLengthOfEmployment: nonNegativeInt,
    carCostsPerMinute: nonNegativeNum,
    otherCostsPerMinute: nonNegativeNum,
  });
};


function AdvancedCostParameters() {

  // Fetch organization id from store
  const organizationId = useUserStore((state) => state.orgId);

  // read advanced cost parameters data from cache
  const { data } = useFragment({
    fragment: OrganizationFragmentDoc,
    from: {
      __typename: "Organization",
      _id: organizationId,
    }
  });
  const advancedCostParametersData = data?.advancedCostParameters;

  // Hooks
  const { t } = useTranslation(["translation", "common"]);

  const { updateAdvancedCostParameterMutation, loading: updateLoading } = useUpdateAdvancedCostParametersMutationHook();

  const initialValues = useMemo(() => ({
    averageHoursWorkedPerMonth: advancedCostParametersData?.averageHoursWorkedPerMonth ?? 0,
    trainingDays: advancedCostParametersData?.trainingDays ?? 0,
    yearlyTrainingCosts: parseFloat(advancedCostParametersData?.yearlyTrainingCosts?.toString() ?? "0") ?? 0,
    sickDays: advancedCostParametersData?.sickDays ?? 0,
    employeeFluctuation: (advancedCostParametersData?.employeeFluctuation ?? 0) / 100,
    yearlyUniformCosts: parseFloat(advancedCostParametersData?.yearlyUniformCosts?.toString() ?? "0") ?? 0,
    otherMaterialCosts: parseFloat(advancedCostParametersData?.otherMaterialCosts?.toString() ?? "0") ?? 0,
    averageLengthOfEmployment: advancedCostParametersData?.averageLengthOfEmployment ?? 0,
    carCostsPerMinute: parseFloat(advancedCostParametersData?.carCostsPerMinute?.toString() ?? "0") ?? 0,
    otherCostsPerMinute: parseFloat(advancedCostParametersData?.otherCostsPerMinute?.toString() ?? "0") ?? 0,
  }), [advancedCostParametersData]);

  const FormSchema = createFormSchema(t);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: advancedCostParametersData ? initialValues : undefined,
    mode: "onChange",
  });

  if (!advancedCostParametersData) {
    return <div>Loading...</div>;
  }


  function onSubmit(data: z.infer<typeof FormSchema>) {
    // update employeeFluctuation value to basis point
    const adjustedData = {
      ...data,
      employeeFluctuation: data.employeeFluctuation * 100,
    };

    updateAdvancedCostParameterMutation({
      variables: {
        acpFields: adjustedData,
      },
    });
  }

  /**
   * Action buttons for FormGroupHeader
   * Buttons are conditionally rendered based on the current state
   */
  const actionButtons: ActionButtonConfig[] = [
    isManager() ? { text: t("common:buttons.save"), actionFn: form.handleSubmit(onSubmit), loading: updateLoading } : null,
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <div className="pb-[300px]">
      <Form {...form}>
        <form>
          <FormGroupHeader
            className="pr-4 pl-2"
            title={t("settings.advancedcosts.title")}
            description={t("settings.advancedcosts.description")}
            actionButtons={actionButtons}
          />
          <div className="pr-4 pl-2 form-field-container" data-cy="settings-advanced-costs-fields-container">
            {
              ADVANCED_COST_PARAMETERS_CONFIG.map((config) => (
                <div className="space-y-2 form-item" key={`advanced-cost-input-${config.name}`}>
                  <Label>
                    <span className={cn(form.formState.errors[config.name] && "text-red-500")}>
                      {t(config.labelKey)}
                    </span>
                    {
                      (config.customTooltip || config.tooltip) && (
                        <HoverCard openDelay={100} closeDelay={0}>
                          <HoverCardTrigger asChild>
                            <Button variant="ghost" size="icon" className={cn("p-0 h-fit w-fit hover:cursor-help", config.tooltipTriggerClassName)} type="button">
                              <InfoCircledIcon className="inline h-4" />
                            </Button>
                          </HoverCardTrigger>
                          <HoverCardContent align="end" className="w-fit">
                            {config.customTooltip || <p className="max-w-[320px]">{config.tooltip}</p>}
                          </HoverCardContent>
                        </HoverCard>
                      )
                    }
                  </Label>
                  <FormattedNumberInputField
                    control={form.control}
                    name={config.name}
                    placeholder={t(config.placeholderKey)}
                    disabled={!isManager()}
                    isInteger={config.isInteger}
                    style={config.style as "decimal" | "currency"}
                  />
                  {form.formState.errors[config.name] && <p className="input-error-message">{form.formState.errors[config.name]?.message}</p>}
                </div>
              ))
            }
          </div>
        </form>
      </Form>
    </div>
  );
}

export default AdvancedCostParameters;
